import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import {
  AuthenticationService,
  ScopeAuthorizationService,
  TenantRouterService,
} from '@mds/ngx-auth';
import { LocalizationService } from '@mds/ngx-core';
import {
  MDS_LAYOUT_NAVIGATION_ITEMS,
  NavigationChildItem,
  NavigationItem,
} from '@mds/ngx-layout';
import { combineLatest, filter, forkJoin, map, switchMap } from 'rxjs';
import { untranslatedNavigationItems } from '../models/navigation-items.model';

export function provideNavigationItems(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: MDS_LAYOUT_NAVIGATION_ITEMS,
      useFactory: navigationGroupsFactory,
      deps: [
        LocalizationService,
        TranslocoService,
        AuthenticationService,
        ScopeAuthorizationService,
        TenantRouterService,
      ],
    },
  ]);
}

export function navigationGroupsFactory(
  localizationService: LocalizationService,
  translateService: TranslocoService,
  authService: AuthenticationService,
  scopeAuthorizationService: ScopeAuthorizationService,
  tenantRouterService: TenantRouterService,
): Signal<NavigationItem[]> {
  return toSignal(
    combineLatest([
      localizationService.translationsLoaded$.pipe(filter(Boolean)),
      translateService.langChanges$,
      authService.isAuthenticated$.pipe(filter(Boolean)),
    ]).pipe(
      switchMap(() => {
        return forkJoin(
          untranslatedNavigationItems.map((item) =>
            scopeAuthorizationService.hasScopes(item.requiredScopes || []).pipe(
              map((visible) =>
                visible
                  ? tenantToMenuMap(tenantRouterService, {
                      ...item,
                      label: translateService.translate(item.label),
                    })
                  : null,
              ),
            ),
          ),
        );
      }),
      map((items) => items.filter((i): i is NavigationItem => i !== null)),
    ),
    { initialValue: [] },
  );
}

function tenantToMenuMap(
  tenantRouterService: TenantRouterService,
  item: NavigationChildItem,
): NavigationItem {
  const tenantLink = item.link
    ? tenantRouterService.addTenant(item.link)
    : undefined;

  return {
    ...item,
    link: tenantLink ?? '',
  };
}
